import React, { useState, useEffect, useRef } from "react";
import {
    mdiArrowLeftRight, mdiTarget, mdiAngleAcute,
    mdiArrowLeft, mdiCheck, mdiClose
} from "@mdi/js";
import intl from "react-intl-universal";
import commonUtils from "../../../../../../script/utils/commonUtils";

import { useInput } from "../../../../../hooks/useInput";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useOnChange } from "../../../../../hooks/useOnChange";

import { Spacer } from "../../../../../components/Spacer";
import { Button } from "../../../../../components/Button";
import { Spinner } from "../../../../../components/Spinner";
import { Checkbox } from "../../../../../components/Checkbox";
import { InputNumber } from "../../../../../components/InputNumber";


const Step = ({ active, children }) => active && <>{children}</>;

export const PlanWizard = props => {
    const { plot, pdf } = props;

    const plansLayer = "plansLayer";
    const wizardLayer = "wizardLayer";

    const angle = useInput("angle", 0);
    const meters = useInput("meters", 0);
    const pdfPageNum = useInput("pdfPageNum", 1);
    const grayscale = useInput("grayscale", false);

    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    const cropFeature = useRef();
    const [refPoint, setRefPoint] = useState();
    const [scaleLine, setScaleLine] = useState();
    const [angleLine, setAngleLine] = useState();
    const [maxNumPages, setMaxNumPages] = useState(1);
    const [rotationAngle, setRotationAngle] = useState(null);
    const [metersPerPoint, setMetersPerPoint] = useState(null);

    useEffect(() => {
        if (!pdf) {
            return;
        };
        const layer = plot.addLayer(wizardLayer, 0);
        plot.layers.forEach(layer => layer.getObjects()
            .forEach(object => object.set({ visible: false })));
        setMaxNumPages(pdf.numPages);
        layer.activate();
        applyCropFeature();
        (async () => {
            await applyPreviewImage(1, grayscale.value);
        })();
    }, [pdf]);

    const debouncedPdfImage = useDebounce(value => {
        setLoading(true);
        //INFO: onSelectPdfPage does not cancel this debounce, so a page change just before
        //applying must not drop a stale preview + crop frame into a later step
        if (step !== 1) {
            return;
        }
        applyPreviewImage(value, grayscale.value);
    }, 800);

    useOnChange((name, value, ignore) => {
        if (!ignore) {
            debouncedPdfImage(value);
        }
    }, [pdfPageNum]);

    const setNextStep = nextStep => {
        const layer = plot.getLayer(wizardLayer);
        removeFeatureObjects(layer);
        //every step registers the handlers it needs, leftovers from the previous step would
        //otherwise stay active (e.g. line drawing after going back to the crop step)
        layer.removeHandlers();
        if (nextStep === 1) {
            applyCropFeature();
        } else if (nextStep === 2) {
            const feature = plot.applyLineFeature(layer, "green", true);
            feature.onSelect(line => setScaleLine(line?.getObjects()[0]));
        } else if (nextStep === 3) {
            const feature = plot.applyLineFeature(layer, "red", true, true);
            feature.onSelect(line => setAngleLine(line?.getObjects()[0]));
        } else if (nextStep === 4) {
            const feature = plot.applyCircleFeature(layer, "green");
            feature.onSelect(circle => setRefPoint(circle));
            feature.onModify(circle => setRefPoint(circle));
        }
        setStep(nextStep);
    };

    const applyCropFeature = () => {
        const layer = plot.getLayer(wizardLayer);
        //the frame is placed automatically around the image, it is never drawn by hand
        cropFeature.current = plot.applyRectFeature(layer, "rgba(0,0,255,0.9)", false, {
            drawable: false
        });
    };

    const placeCropRect = () => {
        const [image] = plot.getLayer(wizardLayer).getObjects();
        const feature = cropFeature.current;
        if (!image || !feature) {
            return;
        }
        const width = image.width * image.scaleX;
        const height = image.height * image.scaleY;
        //INFO: the image is placed with originY "bottom", so its top edge is above its top value
        const bounds = { left: image.left, top: image.top - height, width, height };
        feature.placeRect(bounds);
    };

    const applyPreviewImage = async (page, useGrayscale) => {
        await applyImage(pdf, page, 1, 0.7, undefined, useGrayscale);
        placeCropRect();
    };

    //Converts the crop frame into fractions of the preview image, so the same region can
    //be applied to the higher resolution rendering of the page.
    const getCropRegion = () => {
        const [image] = plot.getLayer(wizardLayer).getObjects();
        const region = cropFeature.current?.getRegion();
        if (!region || !image) {
            return undefined;
        }
        const imageWidth = image.width * image.scaleX;
        const imageHeight = image.height * image.scaleY;
        //INFO: the image is placed with originY "bottom", so its top edge is above its top value
        const imageTop = image.top - imageHeight;
        //the frame can be dragged past the edges of the page, so the region is kept inside it -
        //an area fully outside would otherwise crop down to a single pixel in cropPng
        const x = Math.min(Math.max((region.left - image.left) / imageWidth, 0), 1);
        const y = Math.min(Math.max((region.top - imageTop) / imageHeight, 0), 1);
        const cropRegion = {
            x,
            y,
            width: Math.min(region.width / imageWidth, 1 - x),
            height: Math.min(region.height / imageHeight, 1 - y)
        };
        //an untouched frame covers the whole page, cropping it would only cost time and quality
        const isFullPage = cropRegion.x <= 0.001 && cropRegion.y <= 0.001
            && cropRegion.width >= 0.999 && cropRegion.height >= 0.999;
        return isFullPage ? undefined : cropRegion;
    };

    const removeFeatureObjects = layer => {
        const { feature } = layer;
        if (feature) {
            layer.getObjects().forEach(object => {
                if (object.feature === feature.type) {
                    layer.removeObject(object);
                }
            });
        }
    };

    const onSelectPdfPage = async () => {
        setLoading(true);
        const cropRegion = getCropRegion();
        //the smaller the cropped region, the closer it has to be zoomed in to fill the canvas
        const zoom = cropRegion
            ? Math.min(0.17 / Math.max(cropRegion.width, cropRegion.height), 2)
            : 0.17;
        //INFO: sometimes an error occurs, can be fixed by lowering the scale from 4 to 1
        await applyImage(pdf, pdfPageNum.value, 4, zoom, cropRegion, grayscale.value);
        setLoading(false);
        setNextStep(step + 1);
    };

    const applyImage = async (pdfDoc, page, scale, zoom, cropRegion, useGrayscale) => {
        const pageBase64 = await commonUtils.pdfPageToPng(pdfDoc, page === null ? 1 : page, scale);
        const croppedBase64 = cropRegion
            ? await commonUtils.cropPng(pageBase64, cropRegion)
            : pageBase64;
        const imageBase64 = useGrayscale
            ? await commonUtils.grayscalePng(croppedBase64)
            : croppedBase64;
        const layer = plot.getLayer(wizardLayer);
        layer.removeObjects();
        const imageProperties = {
            originX: "left",
            originY: "bottom",
        };
        const image = await plot.addImage(layer, 0, 0, 0, 1, 1, imageBase64, imageProperties);
        image.set({ evented: false, selectable: false });
        plot.canvas.setViewportTransform([1, 0, 0, 1, 0, image.height]);
        plot.zoom(zoom);
        setLoading(false);
    };

    const onSetScale = () => {
        if (scaleLine) {
            //pythagorean theorem
            const diffX = scaleLine.x2 - scaleLine.x1;
            const diffY = scaleLine.y2 - scaleLine.y1;
            const distHypot = Math.hypot(diffX, diffY);
            setMetersPerPoint(meters.value / distHypot);
            setNextStep(step + 1);
        }
    };

    const onSetAngle = () => {
        if (angleLine) {
            const verAngle = Math.atan2(angleLine.y2 - angleLine.y1, angleLine.x2 - angleLine.x1) * 180 / Math.PI;
            const inpAngle = angle.value;
            const difAngle = 90 - inpAngle;
            let rotAngle = Math.round(verAngle + difAngle) * -1;
            if (rotAngle < 0) {
                rotAngle = 360 - Math.abs(rotAngle);
            }
            const [image] = plot.getLayer(wizardLayer).getObjects();
            image.rotate(rotAngle);
            setRotationAngle(rotAngle);
            setNextStep(step + 1);
        }
    };

    const onSetOrigin = async () => {
        if (refPoint) {
            const point = refPoint.getCenterPoint();
            const [image] = plot.getLayer(wizardLayer).getObjects();
            const centerX = image.width / 2;
            const centerY = image.height / 2;
            const rawX = point.x;
            const rawY = image.height - (point.y * -1);
            const { x: rotX, y: rotY } = plot.rotatePoint(rawX, rawY, centerX, centerY, rotationAngle * -1);
            const p = {
                key: (Math.floor(Math.random() * 90000) + 10000),
                referencePointX: rotX,
                referencePointY: rotY,
                rotationAngle,
                metersPerPoint,
                opacity: 5,
                image: image.getSrc()
            };
            await onComplete(p);
        }
    };

    const onBackToSetImage = async () => {
        pdfPageNum.set(1, true);
        setNextStep(step - 1);
        setLoading(true);
        await applyPreviewImage(1, grayscale.value);
    };

    //applied immediately (unlike the debounced page change) since a checkbox toggle has no
    //intermediate states to coalesce, and grayscale.value would still be stale in this render
    const onToggleGrayscale = async value => {
        grayscale.set(value);
        setLoading(true);
        await applyPreviewImage(pdfPageNum.value, value);
    };

    const onBackToSetScale = () => {
        setMetersPerPoint(null);
        setNextStep(step - 1);
    };

    const onBackToSetAngle = () => {
        setRefPoint(null);
        const [image] = plot.getLayer(wizardLayer).getObjects();
        image.rotate(0);
        plot.canvas.renderAll();
        setRotationAngle(null);
        setNextStep(step - 1);
    };

    const onComplete = async plan => {
        await props.onCompleteWizard(plan);
        reset();
    };

    const onCancel = () => {
        props.onCancelWizard();
        reset();
    };

    const reset = () => {
        plot.getLayer(wizardLayer).removeObjects();
        plot.getLayer(plansLayer).activate();
        plot.layers.forEach(layer => layer.getObjects()
            .forEach(object => object.set({ visible: true })));
        plot.canvas.renderAll();
    };

    return (
        <>
            <Step active={step === 1}>
                <Spinner {...pdfPageNum.bind}
                    min={1}
                    max={maxNumPages}
                    step={1}
                    disabled={loading}
                    title={intl.get("entry.plan.plans.page")} />
                <Spacer variant="horizontal" />
                <Checkbox
                    title={intl.get("entry.plan.plans.grayscale")}
                    value={grayscale.value}
                    disabled={loading}
                    onChange={onToggleGrayscale} />
                <Spacer variant="horizontal" />
                <Button
                    svgIcon={mdiCheck}
                    onClick={onSelectPdfPage}
                    disabled={loading}
                    value={intl.get("common.apply")} />
            </Step>
            <Step active={step === 2}>
                <InputNumber {...meters.bind}
                    title={intl.get("entry.plan.plans.scale")}
                    fractionDigits={2} />
                <Spacer variant="horizontal" />
                <Button
                    svgIcon={mdiArrowLeftRight}
                    onClick={onSetScale}
                    disabled={!scaleLine}
                    value={intl.get("entry.plan.plans.setScale")} />
                <Spacer variant="horizontal" />
                <Button
                    svgIcon={mdiArrowLeft}
                    onClick={onBackToSetImage}
                    value={intl.get("common.back")} />
            </Step>
            <Step active={step === 3}>
                <InputNumber {...angle.bind}
                    title={intl.get("entry.plan.plans.angle")}
                    fractionDigits={2} />
                <Spacer variant="horizontal" />
                <Button
                    svgIcon={mdiAngleAcute}
                    onClick={onSetAngle}
                    disabled={!angleLine}
                    value={intl.get("entry.plan.plans.setAngle")} />
                <Spacer variant="horizontal" />
                <Button
                    svgIcon={mdiArrowLeft}
                    onClick={onBackToSetScale}
                    value={intl.get("common.back")} />
            </Step>
            <Step active={step === 4}>
                <Button
                    svgIcon={mdiTarget}
                    onClick={onSetOrigin}
                    disabled={!refPoint}
                    value={intl.get("entry.plan.plans.setOrigin")} />
                <Spacer variant="horizontal" />
                <Button
                    svgIcon={mdiArrowLeft}
                    onClick={onBackToSetAngle}
                    value={intl.get("common.back")} />
            </Step>
            <Spacer variant="horizontal" />
            <Button
                svgIcon={mdiClose}
                onClick={onCancel}
                disabled={loading}
                value={intl.get("common.cancel")} />
        </>
    );
};